import { useState, useRef, useCallback, useEffect } from 'react';
import { IcUser, IcLock, IcPalette, IcEye, IcEyeOff, IcSearch } from '../../components/ui/Icons';
import useAppStore from '../../store/app.store';
import PageHeader from '../../components/ui/PageHeader';
import toast from 'react-hot-toast';
import api from '../../services/api';

const NIVEL_LABEL = { 1: 'Administrador', 2: 'Gerencial', 3: 'Operacional' };
const NIVEL_COR   = { 1: 'bg-purple-100 text-purple-700', 2: 'bg-blue-100 text-blue-700', 3: 'bg-gray-100 text-gray-600' };

// Forma orgânica como clip-path inline SVG
export const BLOB_STYLE = {
  borderRadius: '28%',
};

// ─── Cropper modal ────────────────────────────────────────────────────────────
function FotoCropper({ src, onSalvar, onCancelar }) {
  const SIZE = 260; // tamanho da área de crop (px)
  const [zoom, setZoom]   = useState(1.2);
  const [pos,  setPos]    = useState({ x: 0, y: 0 });
  const dragging          = useRef(false);
  const lastMouse         = useRef({ x: 0, y: 0 });
  const imgRef            = useRef(null);
  const canvasRef         = useRef(null);

  // Dimensões reais da imagem
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  const onMouseDown = (e) => {
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };
  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setPos(p => ({ x: p.x + dx, y: p.y + dy }));
  }, []);
  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  // Touch support
  const onTouchStart = (e) => {
    dragging.current = true;
    lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.touches[0].clientX - lastMouse.current.x;
    const dy = e.touches[0].clientY - lastMouse.current.y;
    lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setPos(p => ({ x: p.x + dx, y: p.y + dy }));
  }, []);
  const onTouchEnd = useCallback(() => { dragging.current = false; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend',  onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend',  onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  const handleSalvar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width  = SIZE;
      canvas.height = SIZE;
      ctx.clearRect(0, 0, SIZE, SIZE);

      const renderedW = imgSize.w > 0 ? (imgSize.w / imgSize.h) * SIZE * zoom : SIZE * zoom;
      const renderedH = SIZE * zoom;
      const drawX = (SIZE - renderedW) / 2 + pos.x;
      const drawY = (SIZE - renderedH) / 2 + pos.y;

      ctx.drawImage(img, drawX, drawY, renderedW, renderedH);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
      onSalvar(dataUrl);
    };
    img.src = src;
  };

  const renderedW = imgSize.h > 0 ? (imgSize.w / imgSize.h) * SIZE * zoom : SIZE * zoom;
  const renderedH = SIZE * zoom;
  const imgLeft   = (SIZE - renderedW) / 2 + pos.x;
  const imgTop    = (SIZE - renderedH) / 2 + pos.y;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <h3 className="font-bold text-gray-900 mb-1">Ajustar foto</h3>
        <p className="text-xs text-gray-400 mb-4">Arraste para centralizar o rosto · Use o zoom para ajustar</p>

        {/* Área de preview com forma orgânica */}
        <div className="flex justify-center mb-4">
          <div
            style={{ width: SIZE, height: SIZE, overflow: 'hidden', cursor: 'grab', position: 'relative', background: '#e5e7eb', borderRadius: '28%' }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
          >
            {src && (
              <img
                ref={imgRef}
                src={src}
                alt="preview"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: imgLeft,
                  top:  imgTop,
                  width: renderedW,
                  height: renderedH,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs text-gray-400 w-6 flex justify-center"><IcSearch size={13} /></span>
          <input
            type="range" min={0.5} max={3} step={0.05}
            value={zoom}
            onChange={e => setZoom(parseFloat(e.target.value))}
            className="flex-1 accent-brand-600"
          />
          <span className="text-xs text-gray-400 w-8 text-right">{Math.round(zoom * 100)}%</span>
        </div>

        <div className="flex gap-2">
          <button onClick={handleSalvar} className="btn-primary flex-1">Salvar foto</button>
          <button onClick={onCancelar}   className="btn-secondary">Cancelar</button>
        </div>

        {/* Canvas oculto para exportar */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}

// ─── Avatar com forma orgânica ────────────────────────────────────────────────
export function AvatarOrganico({ foto, nome, size = 64, className = '' }) {
  const fontSize = Math.round(size * 0.38);
  return (
    <div
      className={`bg-brand-600 flex items-center justify-center text-white font-black overflow-hidden flex-shrink-0 ${className}`}
      style={{ width: size, height: size, fontSize, ...BLOB_STYLE }}
    >
      {foto
        ? <img src={foto} alt={nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : nome?.charAt(0)
      }
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function Conta() {
  const usuario       = useAppStore(s => s.usuario);
  const updateUsuario = useAppStore(s => s.updateUsuario);
  const tema          = useAppStore(s => s.tema);
  const setTema       = useAppStore(s => s.setTema);

  const [aba, setAba]           = useState('perfil');
  const [cropSrc, setCropSrc]   = useState(null);
  const fileInputRef            = useRef(null);

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('A foto deve ter no máximo 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const atualizarSessao = (dados) => {
    const atual = useAppStore.getState().usuario;
    useAppStore.setState({ usuario: { ...atual, ...dados } });
    updateUsuario(usuario.id, dados);
  };

  const handleSalvarFoto = async (dataUrl) => {
    try {
      const { data } = await api.patch('/auth/perfil', { foto: dataUrl });
      atualizarSessao({ foto: data.foto });
      toast.success('Foto atualizada!');
      setCropSrc(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível salvar a foto.');
    }
  };

  // Perfil
  const [perfil, setPerfil] = useState({
    nome:     usuario?.nome     || '',
    email:    usuario?.email    || '',
    telefone: usuario?.telefone || '',
    bio:      usuario?.bio      || '',
  });
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);

  const handleSalvarPerfil = async (e) => {
    e.preventDefault();
    if (!perfil.nome.trim()) { toast.error('O nome não pode ficar vazio.'); return; }
    setSalvandoPerfil(true);
    try {
      const { data } = await api.patch('/auth/perfil', {
        nome: perfil.nome.trim(), email: perfil.email.trim(),
        telefone: perfil.telefone.trim(), bio: perfil.bio.trim(),
      });
      atualizarSessao({ nome: data.nome, email: data.email, telefone: data.telefone, bio: data.bio });
      toast.success('Perfil atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível salvar o perfil.');
    } finally {
      setSalvandoPerfil(false);
    }
  };

  // Senha
  const [senhaForm, setSenhaForm] = useState({ atual: '', nova: '', confirmar: '' });
  const [showSenhas, setShowSenhas] = useState({ atual: false, nova: false, confirmar: false });
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const handleTrocarSenha = async (e) => {
    e.preventDefault();
    if (!senhaForm.atual) { toast.error('Informe a senha atual.'); return; }
    if (senhaForm.nova.length < 6) { toast.error('A nova senha deve ter ao menos 6 caracteres.'); return; }
    if (senhaForm.nova !== senhaForm.confirmar) { toast.error('As senhas não coincidem.'); return; }
    setSalvandoSenha(true);
    try {
      await api.post('/auth/trocar-senha', { senha_atual: senhaForm.atual, nova_senha: senhaForm.nova });
      toast.success('Senha alterada!');
      setSenhaForm({ atual: '', nova: '', confirmar: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível alterar a senha.');
    } finally {
      setSalvandoSenha(false);
    }
  };

  const toggleShow = (campo) => setShowSenhas(p => ({ ...p, [campo]: !p[campo] }));

  return (
    <div className="max-w-xl">
      {cropSrc && (
        <FotoCropper
          src={cropSrc}
          onSalvar={handleSalvarFoto}
          onCancelar={() => setCropSrc(null)}
        />
      )}

      <PageHeader title="Minha Conta" subtitle="Suas informações pessoais e segurança" />

      {/* Avatar + info rápida */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm mb-6 flex items-center gap-4">
        <div className="relative flex-shrink-0 group">
          <AvatarOrganico foto={usuario?.foto} nome={usuario?.nome} size={72} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
            style={BLOB_STYLE}
          >
            📷
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFotoChange} />
        </div>
        <div>
          <p className="font-bold text-gray-900 text-lg leading-tight">{usuario?.nome}</p>
          <p className="text-sm text-gray-400">{usuario?.cargo}</p>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${NIVEL_COR[usuario?.nivel_acesso]}`}>
            {NIVEL_LABEL[usuario?.nivel_acesso]}
          </span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="block text-xs text-brand-600 hover:text-brand-700 mt-1.5 font-medium"
          >
            Alterar foto
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {[{ key: 'perfil', label: <><IcUser size={13} /> Perfil</> }, { key: 'senha', label: <><IcLock size={13} /> Senha</> }, { key: 'aparencia', label: <><IcPalette size={13} /> Aparência</> }].map(t => (
          <button key={t.key} onClick={() => setAba(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${aba === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {aba === 'perfil' && (
        <form onSubmit={handleSalvarPerfil} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4">
          <div><label className="form-label">Nome completo</label>
            <input className="form-input" value={perfil.nome} onChange={e => setPerfil(p => ({ ...p, nome: e.target.value }))} required /></div>
          <div><label className="form-label">E-mail</label>
            <input type="email" className="form-input" value={perfil.email} onChange={e => setPerfil(p => ({ ...p, email: e.target.value }))} placeholder="seu@email.com" /></div>
          <div><label className="form-label">Telefone / WhatsApp</label>
            <input className="form-input" value={perfil.telefone} onChange={e => setPerfil(p => ({ ...p, telefone: e.target.value }))} placeholder="(81) 9 9999-9999" /></div>
          <div><label className="form-label">Bio / Apresentação</label>
            <textarea className="form-input" rows={3} value={perfil.bio} onChange={e => setPerfil(p => ({ ...p, bio: e.target.value }))} placeholder="Uma frase sobre você ou sua função na equipe..." /></div>
          <div className="pt-2 border-t border-gray-100">
            <button type="submit" disabled={salvandoPerfil} className="btn-primary">
              {salvandoPerfil ? 'Salvando...' : 'Salvar perfil'}
            </button>
          </div>
        </form>
      )}

      {aba === 'senha' && (
        <form onSubmit={handleTrocarSenha} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
            <span className="inline-flex items-center gap-1.5"><IcLock size={12} /> Após alterar a senha, use a nova senha no próximo login.</span>
          </div>
          {[{ key: 'atual', label: 'Senha atual' }, { key: 'nova', label: 'Nova senha' }, { key: 'confirmar', label: 'Confirmar nova senha' }].map(({ key, label }) => (
            <div key={key}><label className="form-label">{label}</label>
              <div className="relative">
                <input type={showSenhas[key] ? 'text' : 'password'} className="form-input pr-10"
                  value={senhaForm[key]} onChange={e => setSenhaForm(p => ({ ...p, [key]: e.target.value }))} required />
                <button type="button" onClick={() => toggleShow(key)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm" tabIndex={-1}>
                  {showSenhas[key] ? <IcEyeOff size={14} /> : <IcEye size={14} />}
                </button>
              </div>
            </div>
          ))}
          <div className="pt-2 border-t border-gray-100">
            <button type="submit" disabled={salvandoSenha} className="btn-primary">
              {salvandoSenha ? 'Alterando...' : 'Alterar senha'}
            </button>
          </div>
        </form>
      )}

      {aba === 'aparencia' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Tema do sistema</h3>
          <p className="text-xs text-gray-400 mb-6">Escolha como prefere visualizar o Legacy Hub.</p>

          <div style={{ display: 'flex', gap: 16 }}>
            {/* Escuro */}
            <button onClick={() => { setTema('escuro'); toast.success('Tema escuro ativado!'); }}
              style={{
                flex: 1, border: `2px solid ${tema === 'escuro' ? '#6366F1' : '#E2E8F0'}`,
                borderRadius: 14, padding: 20, cursor: 'pointer', background: tema === 'escuro' ? '#EEF2FF' : '#F8FAFC',
                transition: 'all .2s', textAlign: 'left',
              }}>
              {/* Preview escuro */}
              <div style={{ background: '#0B1220', borderRadius: 8, padding: 12, marginBottom: 12, height: 80, display: 'flex', gap: 6, overflow: 'hidden' }}>
                <div style={{ width: 24, background: '#131D31', borderRadius: 4 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ height: 8, background: '#19243A', borderRadius: 3, width: '70%' }} />
                  <div style={{ height: 8, background: '#19243A', borderRadius: 3, width: '50%' }} />
                  <div style={{ height: 8, background: '#6366F1', borderRadius: 3, width: '40%', marginTop: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: '#0F172A', margin: 0 }}>Escuro</p>
                  <p style={{ fontSize: 12, color: '#64748B', margin: '2px 0 0' }}>Fundo azul-noite</p>
                </div>
                {tema === 'escuro' && (
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 12 }}>✓</span>
                  </div>
                )}
              </div>
            </button>

            {/* Claro */}
            <button onClick={() => { setTema('claro'); toast.success('Tema claro ativado!'); }}
              style={{
                flex: 1, border: `2px solid ${tema === 'claro' ? '#6366F1' : '#E2E8F0'}`,
                borderRadius: 14, padding: 20, cursor: 'pointer', background: tema === 'claro' ? '#EEF2FF' : '#F8FAFC',
                transition: 'all .2s', textAlign: 'left',
              }}>
              {/* Preview claro */}
              <div style={{ background: '#F1F5F9', borderRadius: 8, padding: 12, marginBottom: 12, height: 80, display: 'flex', gap: 6, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                <div style={{ width: 24, background: '#FFFFFF', borderRadius: 4, border: '1px solid #E2E8F0' }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ height: 8, background: '#E2E8F0', borderRadius: 3, width: '70%' }} />
                  <div style={{ height: 8, background: '#E2E8F0', borderRadius: 3, width: '50%' }} />
                  <div style={{ height: 8, background: '#6366F1', borderRadius: 3, width: '40%', marginTop: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: '#0F172A', margin: 0 }}>Claro</p>
                  <p style={{ fontSize: 12, color: '#64748B', margin: '2px 0 0' }}>Fundo branco</p>
                </div>
                {tema === 'claro' && (
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 12 }}>✓</span>
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
